import React, { useEffect, useState } from 'react'
import _ from 'lodash'
import { connect } from 'react-redux'
import { ThemeProvider as SCThemeProvider } from 'styled-components'
import ColorContrastChecker from 'color-contrast-checker'
import WebFont from 'webfontloader'

import { PrimaryColors } from 'theme/branding/custom-colors'
import {
  darkThemeAlertColors,
  darkThemeGreys,
  lightThemeAlertColors,
  lightThemeGreys,
  trueWhite,
} from 'theme/colors'
import { isSameHexColor } from 'utils/color'
import {
  BRANDING_FONTS_TO_NOT_LOAD,
  BRANDING_FONT_SUBSETS,
} from 'app-constants'
import { Language, mapLanguageToComponentProps } from 'i18n'
import {
  brandDefaults,
  GlobalStyle,
  GlobalBodyColor,
  GlobalCustomLink,
  StyledTheme,
  theme,
} from './index'
import { usePreviousValue } from 'hooks/usePreviousValue'

const ccc = new ColorContrastChecker()

export interface CustomColors extends Partial<PrimaryColors> {
  headerText?: string
  bodyText?: string
  primaryButtonText?: string
  background?: string
}

export interface CustomTheme {
  font?: {
    primary?: string
    header?: string
  }
  color?: CustomColors
  shadow?: Partial<StyledTheme['shadow']>
}

const mapStateToProps = (state: any) => ({
  language: state.global.language,
})

// Determines used grey color schema (grey100 - grey05 colors).
// If background color does have enough contrast with white color (aka background is quite dark), we will use white opaque colors as grey colors. Otherwise, we will use black opaque colors as grey colors.
export const getGreyTheme = (backgroundColor: string) => {
  if (ccc.isLevelAA(backgroundColor, trueWhite)) {
    return darkThemeGreys
  }
  return lightThemeGreys
}

/* Creates alert color theme object.
prefixColor({'130': 'darkred', '100': 'red'}, 'error')
=> {'error130': 'darkred', 'error100': 'red'}
*/
const prefixColors = (
  colorTheme: Record<string, string>,
  prefix: 'success' | 'warn' | 'error' | 'secondary'
): Record<string, string> => {
  return _.reduce(
    colorTheme,
    (result: Record<string, string>, value, key) => {
      return {
        ...result,
        [`${prefix}${key}`]: value,
      }
    },
    {}
  )
}

const hexToRgb = (hex: string) => {
  if (!hex) {
    return undefined
  }
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(
    hex.toLowerCase()
  )
  if (!result) {
    return undefined
  }
  return [
    parseInt(result[1], 16),
    parseInt(result[2], 16),
    parseInt(result[3], 16),
  ]
}

const colorDistance = (color1: number[], color2: number[]) => {
  const rmean = (color1[0] + color2[0]) / 2
  const r = color1[0] - color2[0]
  const g = color1[1] - color2[1]
  const b = color1[2] - color2[2]
  const weightR = 2 + rmean / 256
  const weightG = 4.0
  const weightB = 2 + (255 - rmean) / 256
  return Math.sqrt(weightR * r * r + weightG * g * g + weightB * b * b)
}

const useCustomLinkTheme = (brandColor: string, bodyColor: string) => {
  const maxDistance = Math.sqrt(Math.pow(255, 2) * 3)
  const threshold = maxDistance * 0.3

  const brandColorRgb = hexToRgb(brandColor) || [0, 0, 0]
  const textColorRgb = hexToRgb(bodyColor) || [0, 0, 0]

  const distance = colorDistance(brandColorRgb, textColorRgb)
  if (distance < threshold) {
    return true
  }
  return false
}

export const getCustomAlertColorTheme = (
  backgroundColor: string,
  alertColor130: string,
  colorPrefix: 'success' | 'warn' | 'error' | 'secondary'
) => {
  if (ccc.isLevelAA(backgroundColor, alertColor130)) {
    return {}
  }
  if (ccc.isLevelAA(backgroundColor, trueWhite)) {
    return prefixColors(darkThemeAlertColors, colorPrefix)
  }
  return prefixColors(lightThemeAlertColors, colorPrefix)
}

// Determines used text color.
// If client has not selected custom background => default text color
// If background color has enough contrast with white color => white text
// Otherwise => black
export const getThemeDefaultBodyTextColor = (backgroundColor?: string) => {
  if (!backgroundColor) {
    return undefined
  }
  const rgb = hexToRgb(backgroundColor)
  if (!rgb) {
    return undefined
  }
  const distance = colorDistance(rgb, [255, 255, 255])
  return distance > 128 ? '#FFFFFF' : '#000000'
}

export const ThemeProviderFC: React.FC<{
  useGlobalStyle?: boolean
  customTheme?: CustomTheme
  language?: Language
  customizeLink?: boolean
  children?: React.ReactNode
}> = ({
  customTheme,
  useGlobalStyle = false,
  language = 'en',
  customizeLink = false,
  children,
}) => {
  const [fontLoading, setFontLoading] = useState(false)
  const [fontLoaded, setFontLoaded] = useState(false)
  const [useCustomLink, setUseCustomLink] = useState<boolean | undefined>(
    undefined
  )

  const previousCustomTheme = usePreviousValue(customTheme)

  let customThemeWithGreys = { ...customTheme }

  // If custom theme has background color set, we determine which grey color schema is better with the background color. In case the background color is dark, we will use lighter grey color schema. In case it is light, we will use darker grey color schema.
  if (
    customTheme?.color?.background &&
    !isSameHexColor(customTheme?.color?.background, theme.color.background)
  ) {
    const greyTheme = getGreyTheme(customTheme.color.background)

    const customWarnTheme = getCustomAlertColorTheme(
      customTheme.color.background,
      theme.color.warn130,
      'warn'
    )

    const customErrorTheme = getCustomAlertColorTheme(
      customTheme.color.background,
      theme.color.error130,
      'error'
    )

    const customSuccessTheme = getCustomAlertColorTheme(
      customTheme.color.background,
      theme.color.success130,
      'success'
    )

    const customInfoTheme = getCustomAlertColorTheme(
      customTheme.color.background,
      theme.color.secondary130,
      'secondary'
    )

    const bodyText =
      customTheme.color.bodyText ||
      getThemeDefaultBodyTextColor(customTheme.color.background)

    customThemeWithGreys = {
      ...customTheme,
      color: {
        ...greyTheme,
        ...customWarnTheme,
        ...customErrorTheme,
        ...customSuccessTheme,
        ...customInfoTheme,
        ...customTheme.color,
        bodyText,
      },
    }
  }

  const loadFonts = (fonts: (string | undefined)[]) => {
    if (fontLoaded || fontLoading) {
      return
    }

    const fontsToLoad = fonts.filter(
      font => !!font && !BRANDING_FONTS_TO_NOT_LOAD.includes(font)
    )

    if (fontsToLoad.length === 0) {
      setFontLoaded(true)
      return
    }

    setFontLoading(true)
    const fontSubsets: any = BRANDING_FONT_SUBSETS
    const subsets = fontSubsets[language] || []

    WebFont.load({
      google: {
        families: fontsToLoad.map(
          font => font + (subsets.length > 0 ? `:${subsets.join(',')}` : '')
        ),
      },
      active: () => {
        setFontLoading(false)
        setFontLoaded(true)
      },
    })
  }

  useEffect(() => {
    if (customTheme && customTheme.font) {
      const primaryFont = customTheme.font.primary
      const headerFont = customTheme.font.header
      if (primaryFont || headerFont) {
        loadFonts([primaryFont, headerFont])
      }
    }
    if (customTheme && customTheme.color && customizeLink) {
      const usedTheme = _.merge({}, theme, customThemeWithGreys)
      const brandColor = usedTheme.color.primary110
      const bodyColor = usedTheme.color.bodyText
      const hasBothColors = brandColor && bodyColor
      const previousBodyColor = previousCustomTheme?.color?.bodyText
      const previousBrandColor = previousCustomTheme?.color?.primary110
      const differentThanPrevious =
        brandColor !== previousBrandColor || bodyColor !== previousBodyColor
      if (!previousCustomTheme || (hasBothColors && differentThanPrevious)) {
        const customLink = useCustomLinkTheme(brandColor, bodyColor)
        setUseCustomLink(customLink)
      }
    }
  }, [customTheme, previousCustomTheme])
  const usedTheme = _.merge({}, theme, customThemeWithGreys)

  return (
    <SCThemeProvider theme={usedTheme}>
      {useGlobalStyle && <GlobalStyle />}
      {useGlobalStyle && <GlobalBodyColor />}
      {useCustomLink && <GlobalCustomLink />}
      {children}
    </SCThemeProvider>
  )
}

const ThemeProvider = connect(mapLanguageToComponentProps(mapStateToProps))(
  ThemeProviderFC
)

export default ThemeProvider
